import React, { FC } from "react";
import {
    ArticleTitle,
    TitleBackplate,
    TitleContent,
    TitleHead,
    TitleText,
} from "../../components/articles/header/style";
import { Main } from "../../components/articles";
import {
    ArticleLayout,
    ArticleSpace,
    ArticleText,
    AuthorPubDate,
    Content,
    ContentSubTitle,
    ContentTitle,
    FirstArticleImage,
    Header,
} from "../../components/articles/article/style";
import {
    Facebook,
    LinkedIn,
    RightContent,
    Share,
    ShareHeader,
    ShareSocialList,
    StickyWrapper,
    Telegram,
} from "../../components/articles/right/style";
import LearnMore from "../../components/learn_more";

const FirstArticlePage: FC = () => {
    return (
        <Main>
            <ArticleTitle>
                <TitleBackplate imagePath='/articles/net-zero.svg' />
                <TitleText>
                    <TitleHead>
                        <br />
                        From offsetting to net-zero and reasons for sustainability
                    </TitleHead>
                </TitleText>
            </ArticleTitle>
            <ArticleSpace>
                <ArticleLayout>
                    <Header>
                        <AuthorPubDate>October 03, 2022</AuthorPubDate>
                    </Header>
                    <Content>
                        <ContentSubTitle>
                            1. The climate crisis is no longer a doubt.
                        </ContentSubTitle>
                        <ArticleText>
                            Throughout the history of climate crisis discourse, it has been
                            criticized, ridiculed and discredited. If earlier scientists had to
                            spend time to prove the reality of the threat, now its consequences are
                            easily visible and weakly contested. <br /> <br />
                            <FirstArticleImage
                                src={`${process.env.PUBLIC_URL}/articles/global_temp_anomaly.png`}
                            />
                            <i>
                                [This chart shows temperature rise data from several independent
                                sources.]
                            </i>
                            <br /> <br />
                            Here is how the discourse shifted over the past decades: <br /> <br />{" "}
                            IPCC first report in 1990 – predictions about temperature rising on 0,3
                            °C per decade
                            <br /> IPCC fifth report in 2014 – human influence on the climate system
                            is clear. <br /> IPCC sixth report in 2022 – it is only possible to
                            avoid warming of 1.5 °C (2.7 °F) or 2.0 °C (3.6 °F) if massive and
                            immediate cuts in GHG emissions are made.
                        </ArticleText>
                        <ContentSubTitle>What is IPCC & GHG?</ContentSubTitle>
                        <ArticleText>
                            <b>IPCC</b> is short for Intergovernmental Panel on Climate Change
                            (developed and unified with the support of the UN), which provides
                            regular assessments of the scientific basis of climate change, its
                            impacts and future risks, and options for adaptation and mitigation.{" "}
                            <br /> <br />
                            Greenhouse gases or <b>GHG</b>s, are compound gases that trap heat or
                            longwave radiation in the atmosphere (like carbon dioxide, methane,
                            nitrous oxide, and the fluorinated gases). Their presence in the
                            atmosphere makes the Earth’s surface warmer.The accumulation of GHGs
                            since the industrial revolution has accelerated this greenhouse effect,
                            causing global warming and climate change. Currently greenhouse gas
                            emissions are calculated according to GHG protocol the foundations of
                            which were laid back in 2001.
                        </ArticleText>
                        <ContentSubTitle>
                            2. How does climate change manifest itself ?
                        </ContentSubTitle>
                        <ArticleText>
                            {/* eslint-disable-next-line react/no-unescaped-entities */}
                            In order to understand how the climate crisis is affecting us, let's
                            first
                            {/* eslint-disable-next-line react/no-unescaped-entities */}
                            understand its implications for nature's systems. <br />
                            <br />
                            <ul>
                                <li>
                                    Ocean warming: The world’s oceans, with their significant
                                    capacity for storing heat, absorb around 90% of the earth’s
                                    excess heat. As a result, ocean temperatures are warming up, and
                                    reached record-breaking levels in 2021 (WEF, 2022).
                                    <br />
                                    <br />
                                </li>
                                <li>
                                    Rising global sea levels: Warmer oceans impact other aspects of
                                    the climate system; for example, they alter ocean currents and
                                    raise global sea levels. As water heats up, it expands. In the
                                    last 100years, the global mean sea level has risen by around 20
                                    cm. However, the rate at which sea levels are rising is
                                    accelerating. During the last century, levels increased at an
                                    average of1.4mm per year, but between 2006 and 2015, this rate
                                    has more than doubled (WMO, 2020a; Lindsey, 2020).
                                    <br />
                                    <br />
                                </li>
                                <li>
                                    Melting of ice sheets and glaciers: As oceans have become warmer
                                    and land surface temperatures have increased, the mass of ice
                                    sheets in Greenland and Antarctica have decreased, further
                                    contributing to rising sea levels.Meanwhile, glaciers around the
                                    world are continuing to shrink, with research data suggesting
                                    that a loss of ice volumes was recorded for the 33rd consecutive
                                    year from 2018to 2019 (WMO, 2020a).
                                    <br />
                                    <br />
                                </li>
                                <li>
                                    Shrinking sea ice: The average extent of sea ice in theArctic
                                    has diminished at a rate of approximately 3–4% per decade ever
                                    since satellite records began in 1979 (IPCC, 2014). While the
                                    sea-ice extent in Antarctica has varied considerably in the same
                                    time period, 2019 monthly measurements reached record-level lows
                                    in both the Arctic and the Antarctic(WMO, 2020a).
                                    <br />
                                    <br />
                                </li>
                                <li>
                                    Ocean acidification: As natural carbon sinks, oceans absorb a
                                    substantial amount of CO2 every year, helping to reduce the
                                    impact of accumulating CO2 emissions. However,CO2 absorption
                                    also increases the acidity of the ocean by lowering the level of
                                    surface water, threatening the survival of marine life such as
                                    crustaceans and corals (WMO, 2020).
                                    <br />
                                    <br />
                                </li>
                            </ul>
                        </ArticleText>
                        <ContentSubTitle>3. Climate-related impacts and risks </ContentSubTitle>
                        <ArticleText>
                            Now we compare these manifestations with the risks that follow from
                            them.
                            <br />
                            <br />
                            <ul>
                                <li>
                                    Biodiversity and ecosystems.
                                    <br />
                                    <br />
                                    For example, high temperatures and a lack of rainwater may
                                    result in a severe and continuous drought that destroys local
                                    ecosystems and fuels wildfires, which in turn raises the cost of
                                    food and other essentials and leads to the partial or whole
                                    extinction of some flora and fauna species.
                                    <br />
                                    <br />
                                </li>
                                <li>
                                    Socio-economic.
                                    <br />
                                    <br />
                                    Human and natural systems are genuinely linked; for our
                                    existence, survival, and general well-being, we depend on
                                    natural resources. As a result, the physical effects of climate
                                    change have the potential to significantly affect every human
                                    system, from society and business to individuals. Public health,
                                    food security, infrastructural services, physical assets, and a
                                    variety of other domains are all susceptible to their effects.
                                    Furthermore, intensified pressure in these places has the
                                    potential to trigger social unrest, armed conflict,
                                    climate-related migration, and humanitarian catastrophes in
                                    hard-hit countries. It goes without saying that we cannot
                                    overlook the fact that the human cost of these events has
                                    disproportionately affected citizens of low and lower-middle
                                    income countries. Climate-related disasters claimed the lives of
                                    1.3 million people between 1998 and 2017, while 4.4 billion
                                    others were injured, required medical attention, or lost their
                                    homes or were forced to relocate. (Source: Centre for Research
                                    on the Epidemiology of Disasters)
                                </li>
                            </ul>
                        </ArticleText>
                    </Content>
                    <ContentSubTitle>4. What should we do now? 0 and 51</ContentSubTitle>
                    <ArticleText>
                        {/* eslint-disable-next-line react/no-unescaped-entities */}
                        That's two numbers that can describe the certainly ambitious goal set by
                        Bill Gates for humanity in his book: “How to Avoid a Climate Disaster: The
                        Solutions
                        {/* eslint-disable-next-line react/no-unescaped-entities */}
                        We Have and the Breakthroughs We Need”. "The hardest thing mankind has ever
                        {/* eslint-disable-next-line react/no-unescaped-entities */}
                        done" as he himself put it in an interview. But what is behind these
                        numbers?
                    </ArticleText>
                    <ContentTitle>
                        {'"'} 51 billion tons of greenhouse gasses are added to the atmosphere each
                        year
                    </ContentTitle>
                    <ArticleText>
                        Despite the fact that the coronavirus epidemic slowed travel and economic
                        activity in 2020, the world nevertheless adds 51 billion tons of greenhouse
                        gases (GHG) to the atmosphere on average each year, according to the book.
                        <br /> <br />
                        Gates claims that he bases his evaluation of any green technology or program
                        on the proportion of the planet{"'"}s 51 billion tons of GHG that it can
                        eliminate.
                        <br /> <br />
                        When referring to the energy innovation investment fund in which he has
                        money along with investors like Jeff Bezos, Bill Gates writes, {'"'}At
                        Breakthrough Energy, we fund only technologies that might eliminate at least
                        500 million tons a year if they are successful and fully implemented. That
                        represents about 1% of world emissions.{'"'}
                        <br /> <br />
                        Imperative behind this is to take emissions down to zero. But how can this
                        be done if such large companies and projects cope with such a negligible
                        number of emissions? And the answer is simple – cooperation. Only by uniting
                        business, society, governments and the environment on the path to net-zero
                        can the devastating effects of the climate crisis be avoided.
                    </ArticleText>
                    {/* eslint-disable-next-line react/no-unescaped-entities */}
                    <ContentSubTitle>5. So, what's the plan?</ContentSubTitle>
                    <ArticleText>
                        Net-zero refers to a state in which the greenhouse gases going into the
                        atmosphere are balanced by removal out of the atmosphere. <br /> <br />{" "}
                        While there are differing views about ambition in terms of timing for
                        achieving net zero, there is strong international agreement across the
                        climate community and convenors of the Race to Zero campaign that net zero
                        targets should:
                        <br /> <br />
                        <ul>
                            <li>Reducing global emissions by at least 45% by 2030</li>
                            <li>Reach net zero by 2050</li>
                            <li>Set interim targets</li>
                            <li>Act immediately</li>
                        </ul>
                    </ArticleText>
                    <ContentSubTitle>6. Where do our emissions come from?</ContentSubTitle>
                    <ContentTitle>
                        Not everyone knows, but making things, like cement and steel, is more
                        pestilent than using automobiles or airplanes.
                    </ContentTitle>
                    <ArticleText>
                        When it comes to environmental conservation, power and transportation
                        frequently
                        {/* eslint-disable-next-line react/no-unescaped-entities */}
                        receive the lion's share of focus. For instance, Elon Musk and his company,
                        Tesla, are to thank for the huge excitement surrounding electric
                        automobiles.
                        <br /> <br />
                        And while they are important, there is more to the story. Gates presents a
                        breakdown of different categories and the percentage of total world
                        greenhouse gas emissions each is accountable for in order to help with any
                        comprehensive approach for combating climate change:
                        <br /> <br />
                        <FirstArticleImage
                            src={`${process.env.PUBLIC_URL}/articles/emission_distribution_diagram.png`}
                        />
                    </ArticleText>
                    <ContentSubTitle>
                        7. How about the benefits for businesses and institutes pursuing net-zero?
                    </ContentSubTitle>
                    <ArticleText>
                        Securing the Paris Agreement goal to halve global greenhouse gas emissions
                        by 2030 and reach net zero by 2050 to keep global warming within the limits
                        of 1.5 C is only possible with the participation of governments, financial
                        institutions, and businesses. That’s why we envision the following drivers
                        for creating a pressing need for our class of solutions:
                    </ArticleText>
                    <ContentSubTitle>Governmental drivers</ContentSubTitle>
                    <ArticleText>
                        Governments around the world are introducing special regulations for carbon
                        disclosure. For instance, in the EU these regulations include SFDR NFRD and
                        CSDR. The same regulatory approach applies to the UK and the US, while in
                        Singapore these regulations are currently being elaborated. We already
                        understand that the SGX is using TCFD, one of the international frameworks
                        for sustainability reporting for their listed companies, which should be
                        filed already in 2022.
                    </ArticleText>
                    <ContentSubTitle>Financial drivers</ContentSubTitle>
                    <ArticleText>
                        Financial drivers play an important role in accelerating demand for
                        innovative solutions for carbon disclosure. Companies that successfully
                        implement their net zero strategies are gaining better access to capital
                        because the financial system demonstrates a greater interest in assets with
                        a lower carbon footprint.
                    </ArticleText>
                    <ContentSubTitle>Business drivers</ContentSubTitle>
                    <ArticleText>
                        Implementing net-zero strategies in businesses may lead both to enhance the
                        cost-effectiveness of their operations and new commercialization
                        opportunities through eco marketing initiatives.
                        <br />
                        <br />
                        Summing up, governmental initiatives for setting up climate-related policies
                        provide an enhanced flow of capital into new markets and encourage
                        businesses to aim for ambitious science-based targets, creating a positive
                        ambition loop for the net-zero future.
                        <br />
                        <br />
                        For Offsetted, these drivers provide market growth opportunities for
                        decarbonization solutions, as well as increasing demand for transparent,
                        standard-driven, technology-based products for net-zero transition.
                        <br />
                        <br />
                        With our team, you too can discover your path to sustainability. Just book a
                        consultation and let{"'"}s move towards a net-zero future together!
                        <br />
                        <br />
                    </ArticleText>
                </ArticleLayout>
                <LearnMore />
            </ArticleSpace>
            <RightContent>
                <StickyWrapper>
                    <Share>
                        <ShareHeader>Share the article</ShareHeader>
                        <ShareSocialList>
                            <Facebook
                                href='https://facebook.com/sharer/sharer.php?u=https://offsetted.com/net-zero'
                                target='_blank'
                                rel='noopener noreferrer'
                            />
                            <LinkedIn
                                href='https://www.linkedin.com/shareArticle?url=https%3A//offsetted.com/net-zero'
                                target='_blank'
                                rel='noopener noreferrer'
                            />
                            <Telegram
                                rel='noopener noreferrer'
                                target='_blank'
                                href='https://telegram.me/share/url?url=https://offsetted.com/net-zero'
                            />
                        </ShareSocialList>
                    </Share>
                </StickyWrapper>
            </RightContent>
        </Main>
    );
};

export default FirstArticlePage;
